// src/components/common/NotificationMenu.js
import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  Chip,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  DeveloperBoard as CpuIcon,
} from '@mui/icons-material';
import { useAppData } from '../../contexts/AppDataContext';

// Helper function to get time ago string
const getTimeAgo = (timestamp) => {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now - past;
  
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  } else {
    return 'Just now';
  }
};

// Helper function to get icon for alert type
const getAlertIcon = (type) => {
  switch (type) {
    case 'cpu':
      return <CpuIcon fontSize="small" />;
    case 'memory':
      return <MemoryIcon fontSize="small" />;
    case 'disk':
      return <StorageIcon fontSize="small" />;
    default:
      return <WarningIcon fontSize="small" />;
  }
};

// Helper function to get color for alert type
const getAlertColor = (theme, type) => {
  switch (type) {
    case 'cpu':
      return theme.palette.error.main;
    case 'memory':
      return theme.palette.warning.main;
    case 'disk':
      return theme.palette.info.main;
    default:
      return theme.palette.error.main;
  }
};

const NotificationMenu = () => {
  const theme = useTheme();
  const { alerts, fetchAlerts } = useAppData();
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Mark all alerts as read initially
  const [readAlerts, setReadAlerts] = useState(() => {
    const saved = localStorage.getItem('readAlerts');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Calculate unread alerts count
  useEffect(() => {
    if (alerts && alerts.length) {
      const unread = alerts.filter(alert => !readAlerts.includes(alert.timestamp));
      setUnreadCount(unread.length);
    } else {
      setUnreadCount(0);
    }
  }, [alerts, readAlerts]);
  
  // Fetch alerts when component mounts
  useEffect(() => {
    fetchAlerts();
    
    // Set up periodic refresh
    const intervalId = setInterval(() => {
      fetchAlerts();
    }, 60000); // Refresh every minute
    
    return () => clearInterval(intervalId);
  }, [fetchAlerts]);
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    // Mark all alerts as read when menu closes
    if (alerts && alerts.length) {
      const newReadAlerts = [
        ...readAlerts,
        ...alerts.map(alert => alert.timestamp)
      ];
      
      // Remove duplicates
      const uniqueReadAlerts = [...new Set(newReadAlerts)];
      setReadAlerts(uniqueReadAlerts);
      localStorage.setItem('readAlerts', JSON.stringify(uniqueReadAlerts));
    }
    
    setAnchorEl(null);
  };
  
  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={handleMenuOpen}
          sx={{
            backgroundColor: Boolean(anchorEl) ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
            color: Boolean(anchorEl) ? theme.palette.primary.main : theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            },
          }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))`,
            color: theme.palette.text.primary,
            borderRadius: '10px',
            maxHeight: 400,
            width: 320,
            '& .MuiList-root': {
              padding: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight="medium">Notifications</Typography>
          {unreadCount > 0 && (
            <Chip 
              label={`${unreadCount} New`} 
              size="small"
              sx={{
                backgroundColor: theme.palette.error.main,
                color: '#fff',
                fontWeight: 'medium',
                borderRadius: '10px',
                height: 20,
                fontSize: '0.6875rem',
              }}
            />
          )}
        </Box>
        <Divider sx={{ opacity: 0.1 }} />
        
        {alerts.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          alerts.slice(0, 10).map((alert, index) => {
            const isRead = readAlerts.includes(alert.timestamp);
            const alertColor = getAlertColor(theme, alert.type);
            
            return (
              <MenuItem 
                key={`${alert.hostname}-${alert.type}-${index}`}
                sx={{
                  borderRadius: '8px',
                  py: 1,
                  px: 2,
                  my: 0.5,
                  backgroundColor: isRead ? 'transparent' : alpha(alertColor, 0.05),
                  '&:hover': {
                    backgroundColor: isRead 
                      ? alpha(alertColor, 0.05) 
                      : alpha(alertColor, 0.1),
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                  <Box
                    sx={{
                      color: alertColor,
                      borderRadius: '50%',
                      p: 0.5,
                      mr: 1.5,
                      mt: 0.5,
                      backgroundColor: alpha(alertColor, 0.1),
                    }}
                  >
                    {getAlertIcon(alert.type)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="body2" fontWeight="medium">
                        {alert.type.toUpperCase()} Alert
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                        {getTimeAgo(alert.timestamp)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {alert.message}
                    </Typography>
                    <Chip 
                      label={alert.hostname}
                      size="small"
                      sx={{ 
                        mt: 0.5, 
                        height: 20, 
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontSize: '0.6875rem',
                      }}
                    />
                  </Box>
                </Box>
              </MenuItem>
            );
          })
        )}
        
        {alerts.length > 0 && (
          <Box component="div">
            <Divider sx={{ opacity: 0.1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
              <Typography 
                variant="body2" 
                color="primary"
                sx={{ 
                  cursor: 'pointer',
                  fontWeight: 'medium',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
                onClick={handleMenuClose}
              >
                View All Alerts
              </Typography>
            </Box>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationMenu;